import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, Shield, Sparkles, Scale, Users, Clock, Award, FileText, XCircle } from 'lucide-react';
import PageShell, { SectionTitle } from './PageShell';

export default function ConduiteSuperviseePage() {
  return (
    <PageShell>
      <section className="max-w-5xl mx-auto px-4 lg:px-6 py-10 sm:py-14">
        <nav className="flex items-center gap-2 text-[12px] text-white/50 font-mono-spec mb-6">
          <Link to="/" className="hover:text-white">codevoiture.fr</Link>
          <span className="text-white/25">/</span>
          <Link to="/formation" className="hover:text-white">Formation</Link>
          <span className="text-white/25">/</span>
          <span className="text-white/70">Conduite supervisée</span>
        </nav>

        <SectionTitle
          kicker="Formation officielle · Article R211-5-1 du Code de la route"
          title="Conduite supervisée"
        />
        <p className="mt-5 text-white/75 text-[16px] max-w-3xl leading-relaxed">
          La conduite supervisée permet au candidat âgé de <strong className="text-white">18 ans au minimum</strong>, inscrit dans une école de conduite, de compléter sa formation initiale par une phase de conduite <em>« supervisée »</em> par un accompagnateur — pour passer l&apos;épreuve pratique dans des conditions plus sereines.
        </p>

        {/* Comparatif AAC vs Supervisée */}
        <div className="mt-8 rounded-2xl border border-sky-500/30 bg-gradient-to-br from-sky-950/25 via-[#0d0d0e] to-[#0a0a0b] p-5 sm:p-6" data-testid="aac-vs-supervisee">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-sky-500/50 bg-sky-500/10 text-sky-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
            <Sparkles size={11} /> AAC vs Conduite supervisée · en un coup d&apos;œil
          </div>
          <div className="mt-5 grid md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-red-500/30 bg-red-950/15 p-4">
              <div className="text-red-300 text-[11px] font-mono-spec uppercase tracking-wider font-bold">AAC · Apprentissage anticipé</div>
              <ul className="mt-3 space-y-1.5 text-white/75 text-[13px]">
                <li>• Âge min : <strong className="text-white">15 ans</strong></li>
                <li>• Durée min : <strong className="text-white">1 an</strong> · <strong className="text-white">3 000 km</strong></li>
                <li>• <strong className="text-white">3 RVP</strong> obligatoires (préalable + 2 étapes)</li>
                <li>• Période probatoire <strong className="text-emerald-300">RÉDUITE</strong> (2 ans)</li>
                <li>• Tarif assurance <strong className="text-emerald-300">préférentiel</strong></li>
              </ul>
              <Link to="/formation/conduite-accompagnee-aac" className="mt-4 inline-flex items-center gap-1.5 text-red-300 hover:text-red-200 text-[12.5px] font-semibold">
                Voir page AAC complète →
              </Link>
            </div>
            <div className="rounded-xl border border-sky-500/40 bg-sky-950/20 p-4">
              <div className="text-sky-300 text-[11px] font-mono-spec uppercase tracking-wider font-bold">Conduite supervisée</div>
              <ul className="mt-3 space-y-1.5 text-white/75 text-[13px]">
                <li>• Âge min : <strong className="text-white">18 ans</strong></li>
                <li>• Durée min : <strong className="text-amber-300">aucune</strong> · km min : <strong className="text-amber-300">aucun</strong></li>
                <li>• <strong className="text-white">1 RDV préalable</strong> obligatoire</li>
                <li>• Période probatoire <strong className="text-amber-300">NORMALE</strong> (3 ans)</li>
                <li>• Tarif assurance <strong className="text-amber-300">non préférentiel</strong></li>
              </ul>
              <div className="mt-4 text-white/50 text-[12px] italic">Page actuelle</div>
            </div>
          </div>
        </div>

        {/* À qui s'adresse */}
        <section className="mt-14 scroll-mt-6">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-sky-500/40 bg-sky-500/10 text-sky-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
            <Users size={11} /> Chapitre 1
          </div>
          <h2 className="mt-3 font-serif-editorial text-white text-[30px] sm:text-[36px] leading-tight">À qui s&apos;adresse la conduite supervisée ?</h2>
          <p className="mt-5 text-white/75 text-[14.5px] leading-relaxed">
            L&apos;apprentissage en conduite supervisée des véhicules légers s&apos;adresse aux candidats, <strong className="text-white">de 18 ans au minimum</strong>, qui souhaitent acquérir une expérience de conduite <strong>avant</strong> le passage de l&apos;examen du permis de conduire — ou <strong>après</strong>, en cas d&apos;échec(s) à l&apos;épreuve pratique.
          </p>
          <div className="mt-5 rounded-xl border border-amber-500/30 bg-amber-950/15 p-4 flex items-start gap-3">
            <AlertTriangle size={18} className="text-amber-400 shrink-0 mt-0.5" />
            <p className="text-white/85 text-[13.5px] leading-relaxed">
              Pour vous inscrire, vous devez également obtenir <strong className="text-amber-200">l&apos;accord de l&apos;assureur du véhicule</strong>.
            </p>
          </div>
        </section>

        {/* Conditions d'accès */}
        <section className="mt-14 scroll-mt-6">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
            <CheckCircle2 size={11} /> Chapitre 2
          </div>
          <h2 className="mt-3 font-serif-editorial text-white text-[30px] sm:text-[36px] leading-tight">Conditions d&apos;accès</h2>
          <p className="mt-5 text-white/70 text-[14.5px]">
            On peut choisir la conduite supervisée <strong className="text-white">au moment de l&apos;inscription à l&apos;auto-école</strong> ou <strong className="text-white">à tout moment de la formation</strong>.
          </p>
          <p className="mt-4 text-white/75 text-[14.5px]">Pour y accéder, il faut :</p>
          <ul className="mt-4 space-y-2.5">
            {[
              "Avoir réussi le code de la route (épreuve théorique générale — ETG) ;",
              "Avoir suivi une formation pratique avec un enseignant de l'école de conduite (20 heures minimum) ;",
              "Avoir bénéficié d'une évaluation favorable de la part de son enseignant ECSR et obtenu l'AFFI (Attestation de Fin de Formation Initiale) figurant en annexe du livret d'apprentissage.",
            ].map((item, i) => (
              <li key={i} className="flex gap-3 items-start">
                <CheckCircle2 size={18} className="text-emerald-400 shrink-0 mt-0.5" />
                <span className="text-white/85 text-[14.5px] leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
          <div className="mt-6 rounded-xl border border-sky-500/30 bg-sky-950/15 p-4">
            <p className="text-white/85 text-[13.5px] leading-relaxed">
              📅 Cette période débute par un <strong className="text-sky-200">rendez-vous préalable</strong> qui a lieu en présence de l&apos;enseignant, de l&apos;élève et du futur accompagnateur. L&apos;enseignant dispense alors ses conseils aux deux parties pour bien commencer la période de conduite supervisée.
            </p>
          </div>
        </section>

        {/* Déroulement + avantages */}
        <section className="mt-14 scroll-mt-6">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-red-500/40 bg-red-500/10 text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
            <Clock size={11} /> Chapitre 3
          </div>
          <h2 className="mt-3 font-serif-editorial text-white text-[30px] sm:text-[36px] leading-tight">Déroulement & avantages</h2>
          <div className="mt-6 grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
              <div className="text-white/50 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Déroulé de la formation</div>
              <p className="mt-2 text-white/80 text-[14px] leading-relaxed">
                La conduite supervisée <strong className="text-white">n&apos;impose ni durée minimale, ni distance minimale</strong> à parcourir. La flexibilité totale — c&apos;est ce qui distingue le plus la supervisée de l&apos;AAC.
              </p>
            </div>
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-950/15 p-5">
              <div className="text-emerald-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Avantage principal</div>
              <p className="mt-2 text-white/85 text-[14px] leading-relaxed">
                Acquérir un <strong className="text-white">maximum d&apos;expérience et de confiance</strong> au volant avant le passage de l&apos;épreuve pratique — ou après un échec, pour repartir plus sereinement.
              </p>
            </div>
          </div>
        </section>

        {/* ATTENTION probatoire */}
        <section className="mt-10 rounded-2xl border border-amber-500/40 bg-gradient-to-br from-amber-950/25 via-[#0d0d0e] to-[#0a0a0b] p-5 sm:p-6" data-testid="attention-probatoire">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-amber-500/50 bg-amber-500/10 text-amber-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
            <AlertTriangle size={11} /> Attention · À bien connaître
          </div>
          <h3 className="mt-3 font-serif-editorial text-white text-[24px] sm:text-[28px] leading-tight">Pas de réduction de la période probatoire</h3>
          <p className="mt-3 text-white/80 text-[14px] leading-relaxed">
            Contrairement à l&apos;apprentissage anticipé de la conduite, la conduite supervisée <strong className="text-amber-200">ne permet pas de réduire la durée de la période probatoire</strong>.
          </p>
          <ul className="mt-4 space-y-2 text-white/85 text-[13.5px]">
            <li className="flex gap-2"><span className="text-amber-400">▸</span> Les nouveaux titulaires du permis disposent de <strong className="text-white">6 points</strong> sur leur permis</li>
            <li className="flex gap-2"><span className="text-amber-400">▸</span> Ils doivent attendre <strong className="text-white">3 ans sans infraction</strong> avant d&apos;en obtenir 12 (contre 2 ans en AAC)</li>
            <li className="flex gap-2"><span className="text-amber-400">▸</span> Le candidat ne bénéficie donc <strong className="text-white">pas nécessairement</strong> d&apos;un tarif préférentiel « jeune conducteur »</li>
          </ul>
        </section>

        {/* Règles d'assurance */}
        <section className="mt-14 scroll-mt-6">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-sky-500/40 bg-sky-500/10 text-sky-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
            <Shield size={11} /> Chapitre 4 · Règles incontournables
          </div>
          <h2 className="mt-3 font-serif-editorial text-white text-[30px] sm:text-[36px] leading-tight">Assurance : ce qu&apos;il faut savoir</h2>

          <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <div className="text-white/50 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Demande d&apos;extension de garantie</div>
            <div className="mt-3 space-y-3 text-white/80 text-[13.5px] leading-relaxed">
              <p>Un exemplaire de <strong className="text-white">l&apos;attestation de fin de formation initiale (AFFI)</strong> est transmis à la société d&apos;assurances par le souscripteur du contrat de formation.</p>
              <p>L&apos;accord préalable ÉCRIT de la société d&apos;assurance doit être obtenu sur <strong className="text-white">l&apos;extension de garantie</strong> nécessaire pour la conduite du ou des véhicules utilisés au cours de la future phase de conduite supervisée. Cette extension <strong className="text-emerald-300">n&apos;entraîne pas de surprime</strong>.</p>
              <p>Cet accord précise le ou les <strong className="text-white">noms des accompagnateurs autorisés</strong> par la société d&apos;assurances. Il est joint au contrat de formation de l&apos;élève qui précise les obligations relatives à la fonction d&apos;accompagnateur et les conditions spécifiques à la conduite supervisée (ou à l&apos;avenant au contrat, si le choix a été décidé après la conclusion).</p>
            </div>
          </div>

          <div className="mt-5 rounded-2xl border border-red-500/30 bg-red-950/15 p-5">
            <div className="inline-flex items-center gap-1.5 text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
              <XCircle size={11} /> En cas de refus de l&apos;assurance
            </div>
            <p className="mt-3 text-white/80 text-[13.5px] leading-relaxed">
              L&apos;assureur peut refuser de délivrer cette extension si le candidat a été condamné pour :
            </p>
            <ul className="mt-3 space-y-1.5 text-white/85 text-[13.5px]">
              {[
                "Homicide et blessures involontaires",
                "Conduite sous l'emprise d'un état alcoolique",
                "Délit de fuite",
                "Refus d'obéir à un ordre de s'arrêter émis par les forces de l'ordre",
                "Conduite en période de suspension ou d'annulation du permis",
              ].map((r) => (
                <li key={r} className="flex gap-2"><span className="text-red-400">✕</span> {r}</li>
              ))}
            </ul>
            <p className="mt-4 text-white/70 text-[13px] leading-relaxed">
              L&apos;assureur peut également refuser d&apos;assurer un nouveau conducteur en apprentissage s&apos;il estime que sa conduite présente un <strong className="text-amber-200">risque trop élevé</strong>. Vous devrez alors changer d&apos;assureur pour être couvert.
            </p>
            <p className="mt-3 text-white/60 text-[12.5px] italic">
              Si vous estimez néanmoins que ce refus est discriminant, vous pouvez saisir <strong className="text-sky-300">le médiateur des assurances</strong>.
            </p>
          </div>
        </section>

        {/* Renvoi vers cadre légal complet */}
        <div className="mt-14 rounded-2xl border border-white/10 bg-white/[0.02] p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <Scale size={28} className="text-white/40 shrink-0" strokeWidth={1.4} />
          <div className="flex-1">
            <div className="font-serif-editorial text-white text-[18px]">Cadre légal complet (articles R211-3 à R211-6)</div>
            <p className="text-white/60 text-[13px] mt-1">Consulte la page AAC pour tous les articles du Code de la route + Arrêté du 29 juillet 2013 (livret d&apos;apprentissage) + Arrêté du 20 avril 2012 (examens & délivrance).</p>
          </div>
          <Link
            to="/formation/conduite-accompagnee-aac#textes"
            className="shrink-0 inline-flex items-center gap-2 h-11 px-5 rounded-full border border-white/20 text-white/80 hover:text-white hover:border-white/40 text-[13px] font-semibold transition"
          >
            <FileText size={13} /> Voir les textes de loi
          </Link>
        </div>
      </section>
    </PageShell>
  );
}
